import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

/**
 * TutorialSystem - A unified tutorial/onboarding system for iLing
 * 
 * Features:
 * - Context-sensitive tutorials based on current page
 * - Spotlight highlighting of target elements
 * - Mobile-responsive (points to bottom bar on mobile)
 * - Persistent tutorial completion state
 */

// Tutorial definitions for different pages/contexts
const TUTORIALS = {
  dashboard: {
    title: 'Dashboard Tour',
    steps: [
      {
        target: '[data-testid="nav-home"]',
        mobileTarget: '[data-testid="mobile-nav-home"]',
        title: 'Your Language Dashboard',
        content: 'This is your home base. See all your languages at a glance, track your progress, and choose which language to study today.',
        position: 'right'
      },
      {
        target: '[data-testid="nav-forgePath"]',
        mobileTarget: '[data-testid="mobile-nav-forgePath"]',
        title: 'The Forge Path',
        content: 'Master vocabulary through our gamified lesson system. Progress through Iron, Bronze, Silver, and Gold levels to truly learn each word.',
        position: 'right'
      },
      {
        target: '[data-testid="nav-import"]',
        mobileTarget: '[data-testid="mobile-nav-import"]',
        title: 'Import Texts',
        content: 'Import any text in your target language - articles, books, or your own content. Our reader will help you learn vocabulary in context.',
        position: 'right'
      },
      {
        target: '[data-testid="credit-meter"]',
        title: 'Your Credits',
        content: 'Credits are used for AI-powered features like text generation and translations. You started with 500 free credits!',
        position: 'bottom'
      }
    ]
  },
  reader: {
    title: 'Reader Tutorial',
    steps: [
      {
        target: '[data-testid="text-title"]',
        title: 'Reading Your Text',
        content: 'Click on any word to see its definition, pronunciation, and add it to your vocabulary list. Words are colored by how well you know them.',
        position: 'bottom'
      },
      {
        target: '[data-testid="sentence-mode-btn"]',
        title: 'Sentence Mode',
        content: 'Switch to Sentence Mode to focus on one sentence at a time. Perfect for intensive reading and listening practice.',
        position: 'bottom'
      },
      {
        target: '[data-testid="play-text-btn"]',
        title: 'Listen & Learn',
        content: 'Click the Listen button to hear the entire text read aloud. Great for improving your listening skills and pronunciation.',
        position: 'bottom'
      }
    ]
  },
  popover: {
    title: 'Word Popover Guide',
    steps: [
      {
        target: '[data-testid="play-audio-btn"]',
        title: 'Hear the Word',
        content: 'Click to hear the correct pronunciation. Listening repeatedly helps build your speaking confidence.',
        position: 'top'
      },
      {
        target: '[data-testid="frequency-section"]',
        title: 'Word Frequency',
        content: 'See how common this word is. High-frequency words are more valuable to learn first.',
        position: 'top'
      },
      {
        target: '[data-testid="save-lang-icon"]',
        title: 'Save to Another Language',
        content: 'Learning multiple languages? Save this word to your vocabulary in a different language you\'re studying.',
        position: 'top'
      }
    ]
  },
  forge: {
    title: 'Forge Path Guide',
    steps: [
      {
        target: '[data-testid="node-1"]',
        title: 'Lesson Nodes',
        content: 'Each node is a lesson. Gray means locked, glowing means available. Complete lessons to progress up the path!',
        position: 'right'
      },
      {
        target: '[data-testid="blacksmith-container"]',
        title: 'Your Blacksmith',
        content: 'Your blacksmith companion grows as you complete lessons at Gold level. Watch them evolve through 10 ranks!',
        position: 'left'
      },
      {
        target: '[data-testid="course-selector"]',
        title: 'Four Courses',
        content: 'Each course has 25 lessons. Complete Course 1 to unlock Course 2, and so on. Master all 100 lessons to achieve fluency!',
        position: 'bottom'
      }
    ]
  },
  bible: {
    title: 'Bible Study Guide',
    steps: [
      {
        target: '[data-testid="mode-text"]',
        title: 'Sacred Texts',
        content: 'Study Biblical Hebrew and Greek with original language texts. Click any word to see its Strong\'s Concordance entry.',
        position: 'bottom'
      },
      {
        target: '[data-testid="mode-sentence"]',
        title: 'Sentence Mode',
        content: 'Use Sentence Mode to focus on one verse at a time, with detailed grammar notes and word-by-word analysis.',
        position: 'bottom'
      },
      {
        target: '[data-testid="play-verse-audio"]',
        title: 'Hear the Text',
        content: 'Listen to the pronunciation of Biblical Hebrew or Greek. Essential for proper understanding of ancient texts.',
        position: 'bottom'
      }
    ]
  }
};

// Get tutorial key based on current path
const getTutorialKey = (pathname) => {
  if (pathname.includes('/forge')) return 'forge';
  if (pathname.includes('/bible')) return 'bible';
  if (pathname.includes('/reader') || pathname.includes('/text/')) return 'reader';
  if (pathname.includes('/dashboard') || pathname === '/') return 'dashboard';
  return 'dashboard';
};

// Spotlight overlay component
const Spotlight = ({ targetRect, padding = 8 }) => {
  if (!targetRect) return null;

  const { top, left, width, height } = targetRect;
  
  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      {/* Dark overlay with hole cut out */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={left - padding}
              y={top - padding}
              width={width + padding * 2}
              height={height + padding * 2}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
        />
      </svg>
      {/* Glowing border around target */}
      <div
        className="absolute border-2 border-amber-400 rounded-lg animate-pulse"
        style={{
          top: top - padding,
          left: left - padding,
          width: width + padding * 2,
          height: height + padding * 2,
          boxShadow: '0 0 20px rgba(251, 191, 36, 0.5), 0 0 40px rgba(251, 191, 36, 0.3)'
        }}
      />
    </div>
  );
};

// Tooltip component
const TutorialTooltip = ({ step, position, targetRect, onNext, onPrev, onClose, currentStep, totalSteps, isMobile }) => {
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!targetRect) {
      // Center the tooltip if no target
      setTooltipPos({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 175
      });
      return;
    }

    const { top, left, width, height } = targetRect;
    const tooltipWidth = 350;
    const tooltipHeight = 200;
    const margin = 16;

    let newTop, newLeft;

    switch (position) {
      case 'top':
        newTop = top - tooltipHeight - margin;
        newLeft = left + width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        newTop = top + height + margin;
        newLeft = left + width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        newTop = top + height / 2 - tooltipHeight / 2;
        newLeft = left - tooltipWidth - margin;
        break;
      case 'right':
        newTop = top + height / 2 - tooltipHeight / 2;
        newLeft = left + width + margin;
        break;
      case 'center':
      default:
        newTop = window.innerHeight / 2 - tooltipHeight / 2;
        newLeft = window.innerWidth / 2 - tooltipWidth / 2;
    }

    // Keep within viewport
    newTop = Math.max(margin, Math.min(window.innerHeight - tooltipHeight - margin, newTop));
    newLeft = Math.max(margin, Math.min(window.innerWidth - tooltipWidth - margin, newLeft));

    setTooltipPos({ top: newTop, left: newLeft });
  }, [targetRect, position]);

  return (
    <div
      className="fixed z-[9999] w-[350px] bg-slate-900 border border-amber-500/30 rounded-xl shadow-2xl"
      style={{ top: tooltipPos.top, left: tooltipPos.left }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <span className="font-semibold text-white">{step.title}</span>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-slate-300 leading-relaxed">{step.content}</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t border-slate-700">
        <span className="text-sm text-slate-500">
          {currentStep + 1} of {totalSteps}
        </span>
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button variant="ghost" size="sm" onClick={onPrev}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          <Button
            size="sm"
            onClick={onNext}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            {currentStep < totalSteps - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Tutorial Component
export function Tutorial({ tutorialKey, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const tutorial = TUTORIALS[tutorialKey];
  const steps = tutorial?.steps || [];
  const step = steps[currentStep];

  // Track mobile state
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Find and highlight target element
  useEffect(() => {
    if (!step) return;

    const targetSelector = isMobile && step.mobileTarget ? step.mobileTarget : step.target;
    const targetElement = document.querySelector(targetSelector);

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });

      // Scroll element into view if needed
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetRect(null);
    }
  }, [step, currentStep, isMobile]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete?.();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!tutorial || steps.length === 0) return null;

  return createPortal(
    <>
      <Spotlight targetRect={targetRect} />
      <TutorialTooltip
        step={step}
        position={step.position}
        targetRect={targetRect}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={onComplete}
        currentStep={currentStep}
        totalSteps={steps.length}
        isMobile={isMobile}
      />
    </>,
    document.body
  );
}

// Tutorial Button Component - shows a help button that triggers tutorials
export function TutorialButton({ className = '' }) {
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialKey, setTutorialKey] = useState('dashboard');

  // Update tutorial key based on current location
  useEffect(() => {
    const updateTutorialKey = () => {
      const key = getTutorialKey(window.location.pathname);
      setTutorialKey(key);
    };

    updateTutorialKey();
    
    // Listen for route changes
    window.addEventListener('popstate', updateTutorialKey);
    return () => window.removeEventListener('popstate', updateTutorialKey);
  }, []);

  // Also update on pathname changes (for SPA navigation)
  useEffect(() => {
    const key = getTutorialKey(window.location.pathname);
    setTutorialKey(key);
  });

  const handleComplete = () => {
    setShowTutorial(false);
    // Store completion in localStorage
    const completed = JSON.parse(localStorage.getItem('iling-tutorials-completed') || '{}');
    completed[tutorialKey] = true;
    localStorage.setItem('iling-tutorials-completed', JSON.stringify(completed));
  };

  const tutorial = TUTORIALS[tutorialKey];

  return (
    <>
      <button
        onClick={() => setShowTutorial(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:text-amber-300 transition ${className}`}
        title={tutorial?.title || 'Help'}
      >
        <HelpCircle className="w-5 h-5" />
        <span className="hidden md:inline text-sm">Help</span>
      </button>

      {showTutorial && (
        <Tutorial
          tutorialKey={tutorialKey}
          onComplete={handleComplete}
        />
      )}
    </>
  );
}

// Hook to check if tutorial should auto-show for first-time users
export function useAutoTutorial() {
  const [shouldShow, setShouldShow] = useState(false);
  const [tutorialKey, setTutorialKey] = useState('dashboard');

  useEffect(() => {
    const key = getTutorialKey(window.location.pathname);
    setTutorialKey(key);

    const completed = JSON.parse(localStorage.getItem('iling-tutorials-completed') || '{}');
    if (!completed[key]) {
      // Delay showing tutorial to let page load
      const timer = setTimeout(() => setShouldShow(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const markComplete = () => {
    setShouldShow(false);
    const completed = JSON.parse(localStorage.getItem('iling-tutorials-completed') || '{}');
    completed[tutorialKey] = true;
    localStorage.setItem('iling-tutorials-completed', JSON.stringify(completed));
  };

  return { shouldShow, tutorialKey, markComplete };
}

// Help Pointer - Shows a pulsing arrow pointing to the Help button on first login
export function HelpPointer() {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    // Check if user has seen the help pointer before
    const hasSeenPointer = localStorage.getItem('iling-help-pointer-seen');
    if (hasSeenPointer) {
      return;
    }

    // Find the help button
    const findHelpButton = () => {
      const helpBtn = document.querySelector('button[title*="Help"], button[title*="Tour"]');
      if (helpBtn) {
        const rect = helpBtn.getBoundingClientRect();
        // Position the pointer to the right of the button
        setPosition({
          top: rect.top + rect.height / 2 - 20,
          left: rect.right + 10
        });
        setShow(true);
      }
    };

    // Delay to allow page to render
    const timer = setTimeout(findHelpButton, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('iling-help-pointer-seen', 'true');
  };

  if (!show) return null;

  return createPortal(
    <div 
      className="fixed z-[10000] flex items-center gap-2 animate-pulse"
      style={{ top: position.top, left: position.left }}
    >
      {/* Animated Arrow */}
      <div className="flex items-center">
        <div className="w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-r-[15px] border-r-amber-400 animate-bounce-x"></div>
      </div>
      
      {/* Message Bubble */}
      <div 
        onClick={handleDismiss}
        className="bg-amber-500 text-slate-900 px-4 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-amber-400 transition max-w-[220px]"
      >
        <div className="font-semibold text-sm flex items-center gap-1">
          <Sparkles className="w-4 h-4" />
          Need help?
        </div>
        <p className="text-xs opacity-90">
          Click the Help button for instructions on how to use iLing!
        </p>
        <p className="text-xs mt-1 opacity-70 italic">
          Click to dismiss
        </p>
      </div>
      
      {/* CSS for bounce-x animation */}
      <style>{`
        @keyframes bounce-x {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-5px); }
        }
        .animate-bounce-x {
          animation: bounce-x 1s infinite;
        }
      `}</style>
    </div>,
    document.body
  );
}

export default Tutorial;
